import { useFormikContext } from 'formik';
import { isObjectLike, isEmpty } from 'lodash';
import { number } from 'prop-types';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { Container } from '@hivebrite/design-system/Container';
import {
  FontAwesomeIcon,
  CATEGORIES,
} from '@hivebrite/design-system/FontAwesomeIcon';

import { FilterTypes } from 'hb-react/frontoffice/apps/Filters/constants/FilterTypes';
import { useFiltersContext } from 'hb-react/frontoffice/apps/Filters/contexts/FiltersContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import Grid, { Col, Row } from 'hb-react/shared/components/DesignSystem/Grid';
import ErrorFocus from 'hb-react/shared/components/formik/components/ErrorFocus';
import { useAmplitude } from 'hb-react/shared/hooks/useAmplitude';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import Item from './Item';
import Modal from './Modal';

const FilterIcon = () => (
  <FontAwesomeIcon
    height="16px"
    iconName="sliders"
    iconType={CATEGORIES.SOLID}
  />
);
const SearchIcon = () => (
  <FontAwesomeIcon height="16px" iconName="magnifying-glass" />
);

const SEARCH_DISPLAYABLE_TYPES = [FilterTypes.KEYWORD, FilterTypes.FREE_TEXT];
const FilterCol = ({ filter, locationInputRef, submitForm }) => {
  const displaySearchIcon = SEARCH_DISPLAYABLE_TYPES.includes(filter.type);

  return (
    <Col width={{ m: 1 / 2, l: 1 / 3 }}>
      <Item
        key={filter.name}
        filter={filter}
        wrapperProps={{
          mb: 0,
        }}
        onChange={submitForm}
        inputRef={filter.type === FilterTypes.LOCATION && locationInputRef}
        icon={displaySearchIcon ? SearchIcon : undefined}
      />
    </Col>
  );
};

const MoreFilters = ({ totalSelectedFiltersCount }) => {
  const [modalOpened, setModalOpened] = useState(undefined);
  const { currentUser } = useDefaultAppContexts();
  const toggleModal = useCallback(
    () => setModalOpened((prevOpened) => !prevOpened),
    [setModalOpened],
  );

  const { trackEvent } = useAmplitude();

  useEffect(() => {
    if (modalOpened !== undefined) {
      trackEvent(
        `PeopleDirectory|modal is ${modalOpened ? 'opened' : 'closed'}`,
      );
    }
  }, [modalOpened, trackEvent]);

  return (
    <>
      <Button
        onClick={toggleModal}
        variant="secondary"
        size="large"
        icon={FilterIcon}
        dataTestId="more-filters-button"
        aria-label={__(
          'PeopleDirectoryAccessibility|Open a modal to access more filters',
        )}
      >
        {totalSelectedFiltersCount === 0
          ? __('More filters')
          : __('More filters - %{totalSelectedFiltersCount}').i__({
              totalSelectedFiltersCount,
            })}
      </Button>
      {createPortal(
        modalOpened ? (
          <ThemeProvider theme={currentUser ? 'fo' : 'po'}>
            <Modal onClose={toggleModal} />
          </ThemeProvider>
        ) : null,
        document.body,
      )}
    </>
  );
};

MoreFilters.propTypes = {
  totalSelectedFiltersCount: number.isRequired,
};

const TopBar = ({ fixedControls, activeExternalFilters }) => {
  const { config, filters, resetFilters, locationInputRef } =
    useFiltersContext();
  const { submitForm, values } = useFormikContext();

  const totalSelectedFiltersCount = useMemo(
    () =>
      Object.values(values.query).reduce((total, value) => {
        if (isObjectLike(value)) return isEmpty(value) ? total : total + 1;

        return value ? total + 1 : total;
      }, 0),
    [values.query],
  );

  const displayMoreFilters = config?.type === 'users';

  return (
    <Container
      mx={config?.isFixedToLeft ? 'inherit' : undefined}
      width={
        config?.isFixedToLeft ? { _: '100%', m: 768, l: '100%' } : undefined
      }
    >
      <ErrorFocus />
      <Grid width="100%">
        <Row alignItems={{ _: undefined, m: 'flex-end' }}>
          {filters.top.map((topFilter) => (
            <FilterCol
              key={topFilter.name}
              filter={topFilter}
              locationInputRef={locationInputRef}
              submitForm={submitForm}
            />
          ))}

          <Col width={{ m: 1, l: fixedControls ? 1 / 6 : 1 / 3 }}>
            <Box alignItems="flex-end" height="100%">
              {displayMoreFilters && (
                <MoreFilters
                  totalSelectedFiltersCount={totalSelectedFiltersCount}
                />
              )}
              <Button
                onClick={resetFilters}
                size="large"
                dataTestId="clear-filters-button"
                isDisabled={
                  totalSelectedFiltersCount === 0 && !activeExternalFilters
                }
                ml={displayMoreFilters ? 4 : 0}
              >
                {s__('Filters|Clear all filters')}
              </Button>
            </Box>
          </Col>
          {fixedControls && (
            <Col width={{ m: 1, l: 1 / 6 }}>
              <Box
                alignItems="flex-end"
                justifyContent="flex-end"
                height="100%"
                pb={1}
              >
                {fixedControls}
              </Box>
            </Col>
          )}
        </Row>
      </Grid>
    </Container>
  );
};

export default TopBar;
